import { mediaSuggestionFoldersTranslations } from '@epicuro-next/constants/features/localization';
import {
  defaultDetailsPowerConfig,
  IDetailPageConfig,
  ProductListConfiguration,
  ProductListConfigurationKeys,
  ProductListConfigurations,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { ListType, MediaType } from '@epicuro-next/models/engagement';
import { Filter } from '@epicuro-next/models/filters';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { createSelector } from '@ngrx/store';
import { DateTime, Duration } from 'luxon';

export const selectNotAllowedTypes = selectConfigurationValueFrom<
  ProductListConfigurationKeys.LookbookCreationTypeBlackList,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.LookbookCreationTypeBlackList,
  [MediaType.Youtube, MediaType.Directshow, ListType.Commercial].map((item) =>
    item.toLowerCase(),
  ),
);

export const selectShowProductsAutocompleteEnabled =
  selectConfigurationValueFrom<
    ProductListConfigurationKeys.ShowProductsAutocomplete,
    ProductListConfigurations
  >(
    PRODUCT_LIST_NAMESPACE,
    ProductListConfigurationKeys.ShowProductsAutocomplete,
    true,
  );

export const selectShowDownloadListDataButton = selectConfigurationValueFrom<
  ProductListConfigurationKeys.ShowDownloadDataButton,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.ShowDownloadDataButton,
  false,
);

export const selectShowDownloadImagesButton = selectConfigurationValueFrom<
  ProductListConfigurationKeys.ShowDownloadImagesButton,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.ShowDownloadImagesButton,
  false,
);

export const selectShowMediaScrollButtons = selectConfigurationValueFrom<
  ProductListConfigurationKeys.ShowMediaScrollButtons,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.ShowMediaScrollButtons,
  false,
);

export const selectDisablePromotionNoProducts = selectConfigurationValueFrom<
  ProductListConfigurationKeys.DisablePromotionNoProducts,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.DisablePromotionNoProducts,
  false,
);

export const selectEnableSlideListDeletion = selectConfigurationValueFrom<
  ProductListConfigurationKeys.EnableSlideListDeletion,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.EnableSlideListDeletion,
  false,
);

export const selectAllowEditionListsNotOwned = selectConfigurationValueFrom<
  ProductListConfigurationKeys.AllowEditionListsNotOwned,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.AllowEditionListsNotOwned,
  ProductListConfiguration[
    ProductListConfigurationKeys.AllowEditionListsNotOwned
  ],
);

export const selectRenderStyleIDsInEmails = selectConfigurationValueFrom<
  ProductListConfigurationKeys.RenderStyleIDsOnPromotion,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.RenderStyleIDsOnPromotion,
  false,
);

export const selectAllowSpotlightPromotion = selectConfigurationValueFrom<
  ProductListConfigurationKeys.AllowSpotlightPromotion,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.AllowSpotlightPromotion,
  false,
);

export const selectAllowLookbooks = selectConfigurationValueFrom<
  ProductListConfigurationKeys.AllowLookbooks,
  ProductListConfigurations
>(PRODUCT_LIST_NAMESPACE, ProductListConfigurationKeys.AllowLookbooks, false);

export const selectAllowEpicShow = selectConfigurationValueFrom<
  ProductListConfigurationKeys.ShowEpicShow,
  ProductListConfigurations
>(PRODUCT_LIST_NAMESPACE, ProductListConfigurationKeys.ShowEpicShow, false);

export const selectListOldBulkAddStyleIdInputRule =
  selectConfigurationValueFrom<
    ProductListConfigurationKeys.ListOldBulkAddStyleIdInput,
    ProductListConfigurations
  >(
    PRODUCT_LIST_NAMESPACE,
    ProductListConfigurationKeys.ListOldBulkAddStyleIdInput,
    false,
  );

export const selectVideoUnmute = selectConfigurationValueFrom<
  ProductListConfigurationKeys.VideoUnmute,
  ProductListConfigurations
>(PRODUCT_LIST_NAMESPACE, ProductListConfigurationKeys.VideoUnmute, false);

export const selectDynamicLayoutBuilderEnabled = selectConfigurationValueFrom<
  ProductListConfigurationKeys.DynamicLayoutBuilderEnabled,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.DynamicLayoutBuilderEnabled,
  ProductListConfiguration[
    ProductListConfigurationKeys.DynamicLayoutBuilderEnabled
  ],
);

export const selectProductListLayoutSchemas = createSelector(
  selectConfigurationValueFrom<
    ProductListConfigurationKeys.ListPageLayoutSchemas,
    ProductListConfigurations
  >(
    PRODUCT_LIST_NAMESPACE,
    ProductListConfigurationKeys.ListPageLayoutSchemas,
    ProductListConfiguration[
      ProductListConfigurationKeys.ListPageLayoutSchemas
    ],
  ),
  (schema: string) => {
    try {
      return JSON.parse(schema) as IDetailPageConfig;
    } catch {
      return null;
    }
  },
);

export const selectBestListMinimumMonthsLimit = selectConfigurationValueFrom<
  ProductListConfigurationKeys.BestListMinimumMonthsLimit,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.BestListMinimumMonthsLimit,
  12,
);

export const selectCommercialListToMdoEnabled = selectConfigurationValueFrom<
  ProductListConfigurationKeys.CommercialListToMdoEnabled,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.CommercialListToMdoEnabled,
  false,
);

export const selectBestListMinimumMonthsLimitCalendarMinDate = createSelector(
  selectBestListMinimumMonthsLimit,
  (months: number) =>
    DateTime.utc().minus(Duration.fromObject({ months })).toJSDate(),
);

export const selectListCaptionsLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.listsCaption ?? {},
);

export const selectLisDetailsLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.details ?? {},
);

export const selectCommercialListDetailsLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.detailsCommercial ?? {},
);

export const selectListDetailsPowerLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.detailsPower ?? defaultDetailsPowerConfig,
);

export const selectListPromoteEmailLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.promoteEmail ?? {},
);

export const selectListPromoteSpotlightLayoutSchema = createSelector(
  selectProductListLayoutSchemas,
  (schemas) => schemas?.promoteSpotlight ?? {},
);

export const selectMarkVariantInCsForOpenedList = selectConfigurationValueFrom<
  ProductListConfigurationKeys.MarkVariantInCsForOpenedList,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.MarkVariantInCsForOpenedList,
  false,
);

export const selectBackgroundMediaSuggestionFolders = createSelector(
  selectConfigurationValueFrom<
    ProductListConfigurationKeys.BackgroundMediaSuggestionFolders,
    ProductListConfigurations
  >(
    PRODUCT_LIST_NAMESPACE,
    ProductListConfigurationKeys.BackgroundMediaSuggestionFolders,
    undefined,
  ),
  (schema: string) => {
    try {
      return JSON.parse(schema) as Filter;
    } catch (e) {
      console.error('Failed to parse background media suggestion folders', e);
      return null;
    }
  },
);

export const selectBackgroundMediaSuggestionFoldersTranslated = createSelector(
  selectBackgroundMediaSuggestionFolders,
  translateSuggestionFoldersValues,
);

export const selectMediaSuggestionFolders = createSelector(
  selectConfigurationValueFrom<
    ProductListConfigurationKeys.MediaSuggestionFolders,
    ProductListConfigurations
  >(
    PRODUCT_LIST_NAMESPACE,
    ProductListConfigurationKeys.MediaSuggestionFolders,
    undefined,
  ),
  (schema: string) => {
    try {
      return JSON.parse(schema) as Filter;
    } catch (e) {
      console.error('Failed to parse media suggestion folders', e);
      return null;
    }
  },
);

export const selectMediaSuggestionFoldersTranslated = createSelector(
  selectMediaSuggestionFolders,
  translateSuggestionFoldersValues,
);

function translateSuggestionFoldersValues(filter: Filter) {
  if (filter) {
    return {
      ...filter,
      values: filter?.values.map((value) => ({
        ...value,
        displayName: value.displayName
          ? mediaSuggestionFoldersTranslations[value.displayName] ??
            value.displayName
          : value.displayName,
      })),
    };
  }
  return undefined;
}
